import { useInfiniteQuery } from "@tanstack/react-query";
import { getNextPageParam, getPreviousPageParam } from "../../../utils/api";
import {
  GET_ARTIST_LIST_QUERY_KEY,
  GET_COMIC_LIST_QUERY_KEY,
  GET_RELEASES_LIST_QUERY_KEY,
  GET_BUNDLES_LIST_QUERY_KEY,
  getArtists,
  getComics,
  getReleases,
  getBundles,
} from "../api/browseScreenApi";
import { useAccountData } from "../../../hooks/useAccountData";
import { BrowseTabType } from "../../../types";

export function useBrowseFiltersAPI(
  isOpen: boolean,
  tab: BrowseTabType,
  apiParams: URLSearchParams | null,
  ready: boolean,
) {
  const accountData = useAccountData();

  // Normalize filters for query key - sort params to ensure consistent cache key
  const normalizedFilters = new URLSearchParams(apiParams ?? undefined);
  normalizedFilters.sort();
  const filtersKey = normalizedFilters.toString();

  const bundlesQueryKey = [
    ...GET_BUNDLES_LIST_QUERY_KEY,
    tab,
    accountData?.user?.id,
    filtersKey,
  ];
  const {
    isLoading: isLoadingBundleData,
    data: bundleData,
    error: bundleError,
    hasNextPage: hasNextBundlePage,
    isFetchingNextPage: isFetchingNextBundlePage,
    fetchNextPage: fetchNextBundlePage,
  } = useInfiniteQuery({
    enabled: !isOpen && tab === BrowseTabType.bundles && ready && !!apiParams,
    queryKey: bundlesQueryKey,
    queryFn: (ctx) => {
      const paginatedFilters = new URLSearchParams(apiParams!);
      paginatedFilters.set("p", "" + ctx.pageParam);
      return getBundles(paginatedFilters);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.data?.payload?.pagination;
      if (pagination && pagination.page < pagination.total_pages) {
        return pagination.page + 1;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      const pagination = firstPage?.data?.payload?.pagination;
      if (pagination && pagination.page > 1) {
        return pagination.page - 1;
      }
      return undefined;
    },
    refetchOnWindowFocus: false,
  });

  const artistsQueryKey = [
    ...GET_ARTIST_LIST_QUERY_KEY,
    tab,
    accountData?.user?.id,
    filtersKey,
  ];
  const {
    isLoading: isLoadingArtistData,
    data: artistData,
    error: artistError,
    hasNextPage: hasNextArtistPage,
    isFetchingNextPage: isFetchingNextArtistPage,
    fetchNextPage: fetchNextArtistPage,
  } = useInfiniteQuery({
    enabled: !isOpen && tab === BrowseTabType.creators && ready && !!apiParams,
    queryKey: artistsQueryKey,
    queryFn: (ctx) => {
      const paginatedFilters = new URLSearchParams(apiParams!);
      paginatedFilters.set("p", "" + ctx.pageParam);
      return getArtists(paginatedFilters, ctx.signal);
    },
    initialPageParam: 1,
    getNextPageParam,
    getPreviousPageParam,
    refetchOnWindowFocus: false,
  });

  const comicsQueryKey = [
    ...GET_COMIC_LIST_QUERY_KEY,
    tab,
    accountData?.user?.id,
    filtersKey,
  ];
  const {
    isLoading: isLoadingComicData,
    data: comicData,
    error: comicError,
    hasNextPage: hasNextComicPage,
    isFetchingNextPage: isFetchingNextComicPage,
    fetchNextPage: fetchNextComicPage,
  } = useInfiniteQuery({
    enabled: !isOpen && tab === BrowseTabType.comics && ready && !!apiParams,
    queryKey: comicsQueryKey,
    queryFn: (ctx) => {
      const paginatedFilters = new URLSearchParams(apiParams!);
      paginatedFilters.set("p", "" + ctx.pageParam);
      return getComics(paginatedFilters, ctx.signal);
    },
    initialPageParam: 1,
    getNextPageParam,
    getPreviousPageParam,
    refetchOnWindowFocus: false,
  });

  const releasesQueryKey = [
    ...GET_RELEASES_LIST_QUERY_KEY,
    tab,
    accountData?.user?.id,
    filtersKey,
  ];
  const {
    isLoading: isLoadingReleaseData,
    data: releaseData,
    error: releaseError,
    hasNextPage: hasNextReleasePage,
    isFetchingNextPage: isFetchingNextReleasePage,
    fetchNextPage: fetchNextReleasePage,
  } = useInfiniteQuery({
    enabled: !isOpen && tab === BrowseTabType.releases && ready && !!apiParams,
    queryKey: releasesQueryKey,
    queryFn: (ctx) => {
      const paginatedFilters = new URLSearchParams(apiParams!);
      paginatedFilters.set("p", "" + ctx.pageParam);
      return getReleases(paginatedFilters, ctx.signal);
    },
    initialPageParam: 1,
    getNextPageParam,
    getPreviousPageParam,
    refetchOnWindowFocus: false,
  });

  // Don't include isRefetching in isLoading - background refetches shouldn't
  // show "Refreshing..." overlay. Only initial loads trigger loading state.
  if (tab === BrowseTabType.comics) {
    return {
      ready,
      isLoading: isLoadingComicData,
      data: comicData,
      error: comicError,
      hasNextPage: hasNextComicPage,
      isFetchingNextPage: isFetchingNextComicPage,
      fetchNextPage: fetchNextComicPage,
      queryKey: comicsQueryKey,
    };
  } else if (tab === BrowseTabType.releases) {
    return {
      ready,
      isLoading: isLoadingReleaseData,
      data: releaseData,
      error: releaseError,
      hasNextPage: hasNextReleasePage,
      isFetchingNextPage: isFetchingNextReleasePage,
      fetchNextPage: fetchNextReleasePage,
      queryKey: releasesQueryKey,
    };
  } else if (tab === BrowseTabType.bundles) {
    return {
      ready,
      isLoading: isLoadingBundleData,
      data: bundleData,
      error: bundleError,
      hasNextPage: hasNextBundlePage,
      isFetchingNextPage: isFetchingNextBundlePage,
      fetchNextPage: fetchNextBundlePage,
      queryKey: bundlesQueryKey,
    };
  } else {
    return {
      ready,
      isLoading: isLoadingArtistData,
      data: artistData,
      error: artistError,
      hasNextPage: hasNextArtistPage,
      isFetchingNextPage: isFetchingNextArtistPage,
      fetchNextPage: fetchNextArtistPage,
      queryKey: artistsQueryKey,
    };
  }
}
